import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '../../lib/utils';
import type { Components } from 'react-markdown';

interface MarkdownContentProps {
  content: string;
  className?: string;
}

/**
 * Users often write links without a scheme (e.g. "www.google.com"), which the browser
 * treats as a relative URL and resolves against our own domain. Prepend https:// to
 * schemeless external links so they point outside the app. Leaves anchors (#), absolute
 * paths (/), and known schemes (http, mailto, tel...) untouched.
 */
const normalizeHref = (href?: string): string | undefined => {
  if (!href) return href;
  const trimmed = href.trim();
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed) || trimmed.startsWith('#') || trimmed.startsWith('/')) {
    return trimmed;
  }
  return `https://${trimmed}`;
};

const markdownComponents: Components = {
  h1: ({ children }) => (
    <h1 className="text-xl font-bold mt-4 mb-2">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-lg font-semibold mt-3 mb-2">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-base font-semibold mt-2 mb-1">{children}</h3>
  ),
  p: ({ children }) => (
    <p className="mb-3 last:mb-0">{children}</p>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-gray-300 pl-4 my-3 italic text-gray-500">
      {children}
    </blockquote>
  ),
  a: ({ children, href }) => (
    <a
      href={normalizeHref(href)}
      className="text-blue-500 underline hover:text-blue-700"
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => event.stopPropagation()}
    >
      {children}
    </a>
  ),
  ul: ({ children }) => (
    <ul className="list-disc pl-5 mb-3 space-y-1">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal pl-5 mb-3 space-y-1">{children}</ol>
  ),
  code: ({ className, children, ...props }) => {
    const isBlock = /\n/.test(String(children));
    if (isBlock) {
      return (
        <code
          className={cn('bg-transparent p-0 text-xs font-mono block', className)}
          {...props}
        >
          {children}
        </code>
      );
    }
    return (
      <code
        className={cn('bg-gray-100 rounded px-1 py-0.5 text-xs font-mono', className)}
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: ({ children, ...props }) => (
    <pre className="bg-gray-100 rounded-md p-3 my-3 overflow-x-auto" {...props}>
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <table className="w-full border-collapse my-3 text-sm">{children}</table>
  ),
  thead: ({ children }) => (
    <thead className="bg-gray-50">{children}</thead>
  ),
  th: ({ children }) => (
    <th className="border border-gray-300 px-3 py-1.5 text-left font-semibold">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-gray-300 px-3 py-1.5">
      {children}
    </td>
  ),
  hr: () => <div className="h-px bg-gray-200 my-4" />,
};

export const MarkdownContent = ({ content, className }: MarkdownContentProps) => {
  return (
    <div className={cn('text-sm leading-relaxed', className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {content}
      </ReactMarkdown>
    </div>
  );
};
